<template>
    <LoadingCard v-if="loading && !hasLoaded" />
    <div class="content__inner" v-else>
        <header class="content__title pl-0">
            <h1>Base fertiliser diary
                <small class="pull-right" v-if="block"><strong>Block:</strong> &ldquo;{{ block.name }}&rdquo; ({{ block.variety.name }})</small>
            </h1>

            <div class="actions">
                <b-button :to="{name: 'base-fertiliser.create'}"
                          variant="success btn--icon"
                          v-b-tooltip="'Update Base Fertiliser Diary'">
                    <i class="zmdi zmdi-plus"></i>
                </b-button>
            </div>
        </header>

        <div class="card">
            <div class="bg-primary align-items-center d-flex">
                <h5 class="card-header text-white">Diary Entries</h5>
                <b-pagination align="right" class="my-auto ml-auto mr-2" :total-rows="pagination.total" v-model="pagination.page"
                              :per-page="pagination.limit" />
            </div>
            <VueDatePicker
                    id="date"
                    :config="dateRangeConfig"
                    v-model="dateRange"
                    ref="dateRangePicker"
                    class="form-control form-control-lg text-center bg-light"
                    placeholder="Click to filter entries by spray date"
                    name="date" />

            <b-table stacked="md" :items="tableData" :fields="mainTable.fields" :show-empty="true"
                     :sort-by.sync="mainTable.sortBy"
                     :sort-desc.sync="mainTable.sortDesc"
                     :no-local-sorting="true"
                     :busy="this.loading > 0">

                <template slot="block" slot-scope="row">
                    {{ row.item.block.name }}
                </template>

                <template slot="sprayed_at" slot-scope="row">
                    <span class="text-nowrap">{{ row.item.sprayed_at | datetime_format }}</span>
                </template>

                <template slot="fertilisers" slot-scope="row">
                    <template v-for="entry in [...row.item.entries].sort((a,b) => a.fertiliser.name.localeCompare(b.fertiliser.name))">
                        <span class="badge badge-light mr-1 mb-1">{{ entry.fertiliser.name }}</span>
                    </template>
                </template>

                <template slot="expand" slot-scope="row">
                    <b-button variant="secondary" size="sm" @click.stop="row.toggleDetails">
                        {{ row.detailsShowing ? 'Hide' : 'Show'}} Details
                    </b-button>
                </template>

                <template slot="row-details" slot-scope="row">
                    <b-button variant="outline-secondary" class="btn-block" size="sm" :to="{name: 'base-fertiliser.update', params: {logId: row.item.id}}">
                        <i class="zmdi zmdi-edit"></i> Edit this Diary Entry
                    </b-button>
                    <dl class="row">
                        <dt class="col-12 col-md-2" v-if="row.item.entries.length > 0">Fertilisers Used:</dt>
                        <dd class="col-12 col-md-10" v-if="row.item.entries.length > 0">
                            <b-table small striped stacked="md" :items="row.item.entries" :fields="entriesTable.fields"
                                     :show-empty="true">
                                <template slot="fertiliser" slot-scope="entry">
                                    {{ entry.item.fertiliser.name }}
                                </template>
                                <template slot="rate" slot-scope="entry">
                                    {{ entry.item.rate | number_format }}
                                </template>
                                <template slot="weight" slot-scope="entry">
                                    {{ entry.item.weight | number_format }}
                                </template>
                            </b-table>
                        </dd>

                        <dt class="col-8 col-sm-3 col-md-2">Comments:</dt>
                        <dd class="col-8 col-sm-9 col-md-10">
                            <div v-if="row.item.comments" style="white-space: pre-wrap;">{{ row.item.comments }}</div>
                            <span class="text-muted" v-else>(none)</span>
                        </dd>
                    </dl>
                </template>
            </b-table>
        </div>

    </div>
</template>

<script>
import currentUserQuery from '@views/BaseFertiliserDiary/View/queryCurrentUser.graphql';
import findBlockQuery from '@views/BaseFertiliserDiary/View/queryFindBlock.graphql';
import listBaseFertiliserApplicationsQuery from '@views/BaseFertiliserDiary/View/queryListBaseFertiliserApplications.graphql';
import {format as dateFormat, subMonths} from 'date-fns';

export default {
  name: 'ViewBaseFertiliserDiary',
  props: ['blockId', 'startRange'],
  apollo: {
    currentUser: {
      query: currentUserQuery,
      update: data => data.currentUser,
    },
    block: {
      query: findBlockQuery,
      variables() {
        return {
          input: {
            id: this.blockId,
          },
        };
      },
      skip() {
        return !this.blockId;
      },
      update: data => data.findBlock,
    },
    applications: {
      query: listBaseFertiliserApplicationsQuery,
      variables() {
        const variables = {
          input: {
            page: this.pagination.page,
            limit: this.pagination.limit,
            sort: {
              direction: this.mainTable.sortDesc
                ? 'DESC'
                : 'ASC',
              field: this.mainTable.sortBy,
            },
          },
        };

        if (this.blockId) {
          variables.input.block_id = this.blockId;
        }

        if (this.hasDateRange) {
          variables.input.sprayed_at_gte = this.startDate;
          variables.input.sprayed_at_lte = this.endDate;
        }

        return variables;
      },
      manual: true,
      result({ data, loading }) {
        if (!loading) {
          this.applications = data.filterBaseFertiliserApplications.data;
          this.pagination.page = data.filterBaseFertiliserApplications.meta.page;
          this.pagination.limit = data.filterBaseFertiliserApplications.meta.limit;
          this.pagination.total = data.filterBaseFertiliserApplications.meta.total;
          this.pagination.from = data.filterBaseFertiliserApplications.meta.from;
          this.pagination.to = data.filterBaseFertiliserApplications.meta.to;
        }
      },
    },
  },
  watch: {
    loading(newValue) {
      if (!this.hasLoaded && newValue === 0) {
        this.hasLoaded = true;
      }
    },
  },
  data() {
    const end = new Date();
    const start = subMonths(end, 12);
    const lastYearRange = `${dateFormat(start, 'YYYY-MM-DD')} to ${dateFormat(end, 'YYYY-MM-DD')}`;

    return {
      loading: 0,
      hasLoaded: false,
      currentUser: null,
      block: null,
      dateRange: this.startRange ? this.startRange : lastYearRange,
      applications: [],
      pagination: {
        page: 1,
        limit: this.$tableDefaultPageSize,
        total: 0,
        from: null,
        to: null,
      },
      windDirectionValues: [],
      windSpeedValues: [],
      dateRangeConfig: {
        mode: 'range',
        altInput: true,
        enableTime: false,
        nextArrow: '<i class="zmdi zmdi-long-arrow-right" />',
        prevArrow: '<i class="zmdi zmdi-long-arrow-left" />',
        altFormat: 'M j, Y',
        dateFormat: 'Y-m-d',
      },
      mainTable: {
        sortBy: 'sprayed_at',
        sortDesc: true,
        fields: [
          this.blockId ? false : { key: 'block', label: 'Block' },
          { key: 'sprayed_at', label: 'Application Date', sortable: true },
          { key: 'fertilisers', label: 'Fertilisers Used' },
          { key: 'expand', label: ' ' },
        ].filter(Boolean),
      },
      entriesTable: {
        fields: [
          { key: 'fertiliser', label: 'Fertiliser' },
          { key: 'rate', label: 'Rate(kgs-lts /hec)' },
          { key: 'weight', label: 'Weight(kgs or lts)' },
        ],
      },
    };
  },
  computed: {
    startDate() {
      if (!this.dateRange || !this.dateRange.includes(' to ')) {
        return null;
      }

      return `${this.dateRange.split(' to ')[0]} 00:00:00`;
    },
    endDate() {
      if (!this.dateRange || !this.dateRange.includes(' to ')) {
        return null;
      }

      return `${this.dateRange.split(' to ')[1]} 23:59:59`;
    },
    hasDateRange() {
      return this.startDate && this.endDate;
    },
    tableData() {
      return this.applications.map(item => ({
        ...item,
        _showDetails: false,
      }));
    },
  },
  methods: {
    focusDateRangePicker() {
      this.$refs.dateRangePicker.fp.open();
    },
  },
};
</script>
